/** Chart types supported by coco/render-chart and the interactive chart artifact UI. */
export const CHART_ARTIFACT_TYPE = 'chart' as const;

export type ChartArtifactChartType =
  | 'line'
  | 'bar'
  | 'stacked_bar'
  | 'pie'
  | 'scatter'
  | 'area';

export type ChartArtifactDataset = {
  label: string | null;
  values: number[];
};

export type ChartArtifactData = {
  labels: string[];
  datasets: ChartArtifactDataset[];
};

export type ChartArtifactBreakpoint = {
  label: string;
  description: string | null;
  x_label: string | null;
  label_index: number | null;
  color: string | null;
};

export type ChartArtifactCallout = {
  label: string;
  description: string | null;
  color: string | null;
};

export type ChartArtifactDisplayOptions = {
  show_legend: boolean | null;
  show_data_labels: boolean | null;
  footnote: string | null;
};

/** Payload stored in @@cotera-artifact-data for type "chart". */
export type ChartArtifactPayload = {
  chart_type: ChartArtifactChartType;
  title: string;
  subtitle: string | null;
  data: ChartArtifactData;
  breakpoints?: ChartArtifactBreakpoint[] | null;
  callouts: ChartArtifactCallout[] | null;
  options: ChartArtifactDisplayOptions | null;
};

export function isChartArtifactPayload(
  payload: unknown
): payload is ChartArtifactPayload {
  if (
    payload === null ||
    payload === undefined ||
    typeof payload !== 'object'
  ) {
    return false;
  }
  const p = payload as Record<string, unknown>;
  return (
    typeof p['chart_type'] === 'string' &&
    typeof p['title'] === 'string' &&
    p['data'] !== null &&
    p['data'] !== undefined &&
    typeof p['data'] === 'object'
  );
}

/** Semantic dimension key shared across dashboard charts (e.g. "month", "region"). */
export type ChartDimensionKey = string;

export type ChartDimensionDef = {
  label: string;
};

export type ChartDashboardBindings = {
  x?: ChartDimensionKey | null;
  series?: ChartDimensionKey | null;
};

/** One chart tile inside a linked dashboard artifact. */
export type ChartDashboardChartSpec = {
  chart_id: string;
  title: string;
  chart_type: ChartArtifactChartType;
  subtitle: string | null;
  bindings: ChartDashboardBindings;
  data: ChartArtifactData;
  breakpoints?: ChartArtifactBreakpoint[] | null;
  callouts: ChartArtifactCallout[] | null;
  options: ChartArtifactDisplayOptions | null;
};

export const CHART_DASHBOARD_ARTIFACT_TYPE = 'chart-dashboard' as const;

/** Linked multi-chart dashboard; grows as Coco appends charts with the same dashboard_id. */
export type ChartDashboardPayload = {
  dashboard_id: string;
  title: string | null;
  dimensions: Record<ChartDimensionKey, ChartDimensionDef>;
  charts: ChartDashboardChartSpec[];
};

export function isChartDashboardPayload(
  payload: unknown
): payload is ChartDashboardPayload {
  if (
    payload === null ||
    payload === undefined ||
    typeof payload !== 'object'
  ) {
    return false;
  }
  const p = payload as Record<string, unknown>;
  return (
    typeof p['dashboard_id'] === 'string' &&
    Array.isArray(p['charts']) &&
    p['dimensions'] !== null &&
    p['dimensions'] !== undefined &&
    typeof p['dimensions'] === 'object'
  );
}

export function dashboardChartToArtifactPayload(
  chart: ChartDashboardChartSpec
): ChartArtifactPayload {
  return {
    chart_type: chart.chart_type,
    title: chart.title,
    subtitle: chart.subtitle,
    data: chart.data,
    breakpoints: chart.breakpoints ?? null,
    callouts: chart.callouts,
    options: chart.options,
  };
}
